import { Component, Injector, OnDestroy } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { AfAuctionModel, AfBidModel } from "@pattonair/auction-framework";
import { MfBaseComponent, mfStringIsEmptyOrWhitespace, MfTypeInfo, mfTypeIsNullOrUndefined, mfTypeIsUndefined } from "@pattonair/material-framework";
import { map, Observable } from "rxjs";
import { AucRouteNames } from "../route.names";
import { AucAuctionService } from "../services/auction.service";

const TYPE_INFO: MfTypeInfo = { className: "AucAuctionDetailComponent" };

@Component({
  selector: "ecom-auction-detail",
  templateUrl: "./auction.detail.component.html",
  styleUrls: ["./auction.detail.component.scss"]
})
export class AucAuctionDetailComponent extends MfBaseComponent implements OnDestroy {
  protected _auction?: AfAuctionModel;

  public constructor(
    protected override _injector: Injector,
    protected _activatedRoute: ActivatedRoute,
    protected _auctionsService: AucAuctionService,
  ) {
    super(TYPE_INFO, _injector);
    this._getRouteParams();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    if (mfTypeIsUndefined(this._auction) || mfStringIsEmptyOrWhitespace(this._auction.id)) {
      return;
    }
    this._sub(
      this._auctionsService.unsubscribeFromAuction(this._auction.id),
      {
        next: () => this._sub(this._auctionsService.stopConnection()),
      },
    );
  }

  protected _subscribeToChanges(): void {
    if (mfTypeIsNullOrUndefined(this._auction)) {
      console.warn("Auction should not be null when viewing the details.");
      return;
    }
    this._sub(this._auctionsService.startConnection(), {
      next: () => {
        if (mfTypeIsUndefined(this._auction) || mfStringIsEmptyOrWhitespace(this._auction.id)) {
          return;
        }
        this._sub(this._auctionsService.subscribeToAuction(this._auction.id), {
            next: () => {

              this._sub(this._auctionsService.auctionUpdated, {
                next: (message) => {
                  if (mfTypeIsUndefined(this._auction) || mfStringIsEmptyOrWhitespace(this._auction.id) || this._auction.id !== message.id) {
                    return;
                  }
                  this._auction = {
                    ...this._auction,
                    currentPrice: message.currentPrice,
                    bidCount: message.bidCount,
                    reservePriceMet: message.isReservePriceMet,
                    status: message.auctionStatus,
                  };
                },
              });

              this._sub(this._auctionsService.winningUpdate, {
                next: (message) => {
                  if (mfTypeIsUndefined(this._auction) || mfStringIsEmptyOrWhitespace(this._auction.id) || this._auction.id !== message.id) {
                    return;
                  }
                  this._auction = {
                    ...this._auction,
                    currentPrice: message.currentPrice,
                    bidCount: message.bidCount,
                    reservePriceMet: message.isReservePriceMet,
                    status: message.auctionStatus,
                    bidStatus: message.bidStatus,
                  };
                },
              });
            },
          },
        );
      },
    });
  }

  protected _getRouteParams(): void {
    this._sub(this._activatedRoute.params, {
      next: (params) => {
        const auctionId = params[AucRouteNames.parameterNameAuctionId];
        if (mfStringIsEmptyOrWhitespace(auctionId)) {
          return;
        }
        const loading = this._subLoading(this._getAuction(auctionId), {
          next: () => {
            loading.complete();
          },
          error: () => {
            loading.complete();
          },
        });
      },
    });
  }

  protected _getAuction(auctionId: string): Observable<void> {
    return this._auctionsService.getLive(auctionId).pipe(
      map((auction) => {
        this._auction = auction;
        this._subscribeToChanges();
        return;
      }),
    );
  }

  protected _onBidPlaced(bid: AfBidModel): void {
    if (mfTypeIsUndefined(this._auction) || mfStringIsEmptyOrWhitespace(this._auction.id)) {
      return;
    }
    const loading = this._subLoading(this._auctionsService.bid(bid), {
      next: () => {
        loading.complete();
      },
      error: () => {
        loading.complete();
      },
    });
  }
}
